import { useEffect,useMemo } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import type { ArenaDef } from './content'
import { WORLD_COMPOSITIONS } from './generatedWorldExpansion'
import { WORLD_FORGE_COMPOSITIONS } from './generatedWorldForge'
import { constructionBlueprint,constructionPalette,type ConstructionFamily,type ConstructionInput } from './worldConstructionLogic'

type LogicalPiece=ConstructionInput&{family:ConstructionFamily}

function piecesForArena(arenaId:string):LogicalPiece[]{
  const expanded=WORLD_COMPOSITIONS[arenaId]
  if(expanded)return expanded.pieces.map(piece=>({
    id:piece.id,asset:piece.asset,position:piece.position,height:piece.height,motion:piece.motion,family:expanded.family,
  }))
  const forge=WORLD_FORGE_COMPOSITIONS[arenaId as keyof typeof WORLD_FORGE_COMPOSITIONS]
  if(!forge)return[]
  const family=arenaId as ConstructionFamily
  return (Object.values(forge.layers).flat() as Array<{id:string;asset:string;position:readonly [number,number,number];height:number}>)
    .map(piece=>({id:piece.id,asset:piece.asset,position:piece.position,height:piece.height,motion:'static' as const,family}))
}

function StructuralSupport({piece}:{piece:LogicalPiece}){
  const plan=constructionBlueprint(piece,piece.family)
  if(!plan.structural)return null
  const palette=constructionPalette(piece.family)
  const baseY=.06
  const supportSpread=Math.max(.35,plan.foundationRadius*.58)
  const supports=Array.from({length:plan.supportCount},(_,index)=>{
    const pair=index%2===0?-1:1
    const depth=index<2?0:.62
    return [pair*supportSpread,plan.supportHeight/2,-depth*supportSpread] as [number,number,number]
  })
  const accessLength=plan.access?Math.max(0,plan.accessEndZ-plan.accessStartZ):0
  const accessCenter=plan.accessStartZ+accessLength/2
  return <group
    name={'construction-logic:'+piece.id}
    position={piece.position as [number,number,number]}
    userData={{constructionLogic:true,kind:plan.kind,purpose:plan.purpose,loadBearing:plan.loadBearing}}
  >
    <mesh position={[0,baseY,0]} receiveShadow>
      <cylinderGeometry args={[plan.foundationRadius*1.04,plan.foundationRadius,baseY*2,plan.kind==='tower'?12:8]}/>
      <meshStandardMaterial color={palette.foundation} roughness={.76} metalness={piece.family==='capital'?.38:.16}/>
    </mesh>
    {supports.map((position,index)=><mesh key={index} position={position} castShadow receiveShadow>
      <boxGeometry args={[.18+plan.foundationRadius*.08,plan.supportHeight,.18+plan.foundationRadius*.08]}/>
      <meshStandardMaterial color={palette.structure} roughness={.66} metalness={piece.family==='capital'?.42:.18}/>
    </mesh>)}
    {plan.access&&accessLength>.3&&<mesh
      position={[0,.055,accessCenter-piece.position[2]]}
      receiveShadow
    >
      <boxGeometry args={[Math.max(.8,plan.foundationRadius*.72),.1,accessLength]}/>
      <meshStandardMaterial color={palette.foundation} roughness={.72} metalness={piece.family==='capital'?.3:.1}/>
    </mesh>}
    {plan.utilityGlow&&<mesh rotation={[-Math.PI/2,0,0]} position={[0,.14,0]}>
      <ringGeometry args={[Math.max(.28,plan.foundationRadius*.48),Math.max(.32,plan.foundationRadius*.52),32]}/>
      <meshBasicMaterial color={palette.accent} transparent opacity={.2} blending={THREE.AdditiveBlending} depthWrite={false}/>
    </mesh>}
  </group>
}

export function WorldConstructionLogicLayer({arena,renderGeometry=false}:{arena:ArenaDef;renderGeometry?:boolean}){
  const {scene}=useThree()
  const pieces=useMemo(()=>piecesForArena(arena.id),[arena.id])
  const plans=useMemo(()=>pieces.map(piece=>({id:piece.id,plan:constructionBlueprint(piece,piece.family)})),[pieces])
  useEffect(()=>{
    scene.userData.inyronConstructionLogic={
      arenaId:arena.id,pieceCount:pieces.length,
      structuralCount:plans.filter(entry=>entry.plan.structural).length,
      renderGeometry,
      plans:plans.map(entry=>({id:entry.id,kind:entry.plan.kind,purpose:entry.plan.purpose,loadBearing:entry.plan.loadBearing})),
    }
  },[arena.id,pieces.length,plans,renderGeometry,scene])
  if(!pieces.length)return null
  return <group name={'world-construction-logic:'+arena.id} userData={{constructionAuthority:true,renderGeometry,pieceCount:pieces.length}}>
    {renderGeometry&&pieces.map(piece=><StructuralSupport key={piece.id} piece={piece}/>)}
  </group>
}
